import { observer } from "mobx-react-lite";
import React from "react";

import { compareDatesPredicate } from "@bps/date-time";
import { Spinner, Stack } from "@bps/fluent-ui";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { ClaimAdditionalDiagnosesBadge } from "./ClaimAdditionalDiagnosesBadge.tsx";
import { ClaimConsultsRemainingBadge } from "./ClaimConsultsRemainingBadge.tsx";
import { ClaimNumberBadge } from "./ClaimNumberBadge.tsx";
import { ClaimOnsetBadge } from "./ClaimOnsetBadge.tsx";

interface ClaimConsultBadgesProps {
  episodeOfCare: EpisodeOfCare;
  clinicalRecord: ClinicalRecord;
  encounter?: Encounter;
  hideConsultOnNoClaim?: boolean;
  showConsultsRemaining?: boolean;
  showConsultIndex?: boolean;
}

export const ClaimConsultBadges: React.FC<ClaimConsultBadgesProps> = observer(
  props => {
    const { clinical } = useStores();

    const {
      episodeOfCare,
      clinicalRecord,
      encounter,
      hideConsultOnNoClaim,
      showConsultIndex
    } = props;

    const getConsultNumber = async () => {
      const claim = episodeOfCare?.claim;

      if (!showConsultIndex || (hideConsultOnNoClaim && !claim?.claimNumber)) {
        // Don't bother loading the data because the text won't be displayed
        return undefined;
      }

      const appointmentVisits = claim?.appointmentVisits;

      if (
        !claim ||
        !appointmentVisits ||
        appointmentVisits.length === 0 ||
        !encounter ||
        !clinicalRecord.patient?.id
      ) {
        return undefined;
      }

      const eoc = encounter.episodeOfCareId;

      const encounters = await clinical.getEncounters({
        patientId: clinicalRecord.patient?.id,
        episodeOfCareIds: eoc ? [eoc] : undefined
      });

      // Reorganize encounters by their date time.
      const encountersOrdered = encounters.sort((a, b) =>
        compareDatesPredicate(a.startDateTime, b.startDateTime)
      );

      const encounterIndex = encountersOrdered.indexOf(encounter);
      if (encounterIndex !== -1) {
        return `Consult ${encounterIndex + 1}/${encountersOrdered.length}`;
      }

      return undefined;
    };

    return (
      <Stack
        styles={{
          root: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }
        }}
      >
        <DataFetcher
          fetch={getConsultNumber}
          fallback={
            <Stack horizontalAlign="center">
              <Spinner />
            </Stack>
          }
        >
          {consultNumberText => (
            <>
              <ClaimNumberBadge episodeOfCare={episodeOfCare} />
              <ClaimConsultsRemainingBadge
                {...props}
                consultNumberText={consultNumberText}
              />
              <ClaimOnsetBadge episodeOfCare={episodeOfCare} />
              <ClaimAdditionalDiagnosesBadge episodeOfCare={episodeOfCare} />
            </>
          )}
        </DataFetcher>
      </Stack>
    );
  }
);
